import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type Paginated } from '@/lib/api';
import type { TastingNote } from './tastingOptions';

export function useTastingNotes(wineId: number | undefined) {
  return useQuery({
    enabled: !!wineId,
    queryKey: ['tasting-notes', { wine_id: wineId }],
    queryFn: async () => {
      const { data } = await api.get<Paginated<TastingNote>>('/tasting-notes', {
        params: { wine_id: wineId, per_page: 50 },
      });
      return data.data;
    },
  });
}

export function useTastingNote(id: number | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['tasting-note', id],
    queryFn: async () => {
      const { data } = await api.get<{ data: TastingNote }>(`/tasting-notes/${id}`);
      return data.data;
    },
  });
}

export function useSaveTastingNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: number; payload: Partial<TastingNote> }) => {
      if (id) {
        const { data } = await api.put<{ data: TastingNote }>(`/tasting-notes/${id}`, payload);
        return data.data;
      }
      const { data } = await api.post<{ data: TastingNote }>('/tasting-notes', payload);
      return data.data;
    },
    onSuccess: (note) => {
      qc.invalidateQueries({ queryKey: ['tasting-notes', { wine_id: note.wine_id }] });
      qc.invalidateQueries({ queryKey: ['tasting-note', note.id] });
      qc.invalidateQueries({ queryKey: ['wine', note.wine_id] });
    },
  });
}

export function useDeleteTastingNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/tasting-notes/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasting-notes'] });
    },
  });
}
